import { Link } from "react-router-dom";
import { Car, Shield, Wrench, Users, Star, CheckCircle2, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import QuoteForm from "@/components/QuoteForm";
import { trackWhatsApp } from "@/components/layout/WhatsAppButton";

const HERO_IMG = "https://images.unsplash.com/photo-1738996596694-93a6a2359b22?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTF8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBjYXIlMjBoaWdod2F5JTIwbGFuZHNjYXBlfGVufDB8fHx8MTc3MzQzMjMzMHww&ixlib=rb-4.1.0&q=85";

const coverages = [
  { icon: Shield, title: "Cobertura Compreensiva", desc: "Proteção contra roubo, furto, colisão, incêndio e danos naturais." },
  { icon: Users, title: "Danos a Terceiros", desc: "Cobertura para danos materiais e corporais causados a outros." },
  { icon: Wrench, title: "Assistência 24h", desc: "Guincho, chaveiro, troca de pneus e socorro mecânico." },
  { icon: Car, title: "Carro Reserva", desc: "Veículo substituto enquanto o seu está em reparo." },
];

const benefits = [
  "Comparação entre mais de 10 seguradoras",
  "Melhor custo-benefício para o seu perfil",
  "Atendimento consultivo personalizado",
  "Suporte completo em caso de sinistro",
  "Renovação com bônus de desconto",
  "Cobertura para vidros, faróis e retrovisores",
];

export default function SeguroAuto() {
  return (
    <div data-testid="seguro-auto-page">
      {/* Hero */}
      <section className="relative pt-20">
        <div className="bg-navy">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
            <div className="grid lg:grid-cols-2 gap-10 items-center">
              <div>
                <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 rounded-full px-3 py-1 mb-5">
                  <Car className="h-3.5 w-3.5 text-gold" />
                  <span className="text-xs text-white/80 font-medium">Seguro Auto</span>
                </div>
                <h1 className="text-4xl sm:text-5xl font-heading font-bold text-white leading-tight mb-5">
                  Seguro Auto para <span className="text-gold">todo o Brasil</span>
                </h1>
                <p className="text-base md:text-lg text-slate-300 leading-relaxed mb-8">
                  Proteja seu veículo com as melhores coberturas do mercado. Comparamos opções de mais de 10 seguradoras para encontrar o melhor preço para você.
                </p>
                <div className="flex flex-col sm:flex-row gap-3">
                  <Link to="/cotacao">
                    <Button className="btn-gold h-11 px-6" data-testid="auto-cta-cotacao">Solicitar Cotação <ArrowRight className="h-4 w-4 ml-2" /></Button>
                  </Link>
                  <a href="https://wa.me/553125550207?text=Ol%C3%A1%2C%20preciso%20de%20cota%C3%A7%C3%A3o%20de%20seguro%20auto." target="_blank" rel="noopener noreferrer" onClick={() => trackWhatsApp('seguro_auto')}>
                    <Button className="btn-whatsapp h-11 px-6" data-testid="auto-cta-whatsapp">Falar com Especialista</Button>
                  </a>
                </div>
              </div>
              <div className="hidden lg:block">
                <img src={HERO_IMG} alt="Seguro Auto" className="rounded-2xl shadow-2xl w-full h-72 object-cover" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Coverages */}
      <section className="py-20 md:py-28" data-testid="auto-coverages">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <p className="text-sm font-semibold text-gold uppercase tracking-wider mb-3">Coberturas</p>
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-navy">O que o seguro auto cobre</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {coverages.map((c, i) => (
              <div key={i} className="bg-white border border-slate-200 rounded-xl p-6 hover:shadow-lg hover:-translate-y-1 transition-all">
                <div className="w-11 h-11 rounded-lg bg-brand/10 flex items-center justify-center mb-4">
                  <c.icon className="h-5 w-5 text-brand" />
                </div>
                <h3 className="font-heading font-semibold text-navy mb-2">{c.title}</h3>
                <p className="text-sm text-slate-500 leading-relaxed">{c.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 md:py-28 bg-slate-50" data-testid="auto-benefits">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-sm font-semibold text-gold uppercase tracking-wider mb-3">Vantagens</p>
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-navy mb-6">Por que fazer seguro auto com a Núcleo?</h2>
              <ul className="space-y-4">
                {benefits.map((b, i) => (
                  <li key={i} className="flex items-start gap-3 text-slate-700">
                    <CheckCircle2 className="h-5 w-5 text-brand shrink-0 mt-0.5" />
                    <span className="text-base">{b}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-white rounded-2xl border p-6 md:p-8 shadow-sm">
              <h3 className="font-heading font-semibold text-navy text-xl mb-5">Cotação Rápida - Seguro Auto</h3>
              <QuoteForm preselect="Seguro Auto" />
            </div>
          </div>
        </div>
      </section>

      {/* Insurers */}
      <section className="py-16 bg-white" data-testid="auto-insurers">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h3 className="text-xl font-heading font-semibold text-navy mb-3">Seguradoras que comparamos para você</h3>
          <p className="text-sm text-slate-500 mb-8">Porto Seguro, Tokio Marine, HDI, Allianz, Bradesco, Azul Seguros, Zurich, Suhai, Yelum e Mitsui Sumitomo</p>
          <Link to="/seguradoras">
            <Button variant="outline" className="border-brand text-brand hover:bg-brand hover:text-white" data-testid="auto-see-partners">
              Conheça nossas seguradoras parceiras <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
